// ═══════════════════════════════════════════════════════════════════════════
// Combined AI-command boundary schema — SCHEMA-ONLY (no dispatch path)
// ═══════════════════════════════════════════════════════════════════════════
//
// `aiCommandSchema` is the COMBINED discriminated union across all AI
// domains (block / entity / draw-modify / view-space / matchprop / base-prim
// / T7's layer-management) — built by spreading each domain union's
// `.options` so there is ONE union and it stays in lock-step with the
// domains automatically (no re-listed members). The safe contour parses
// every proposal through it BEFORE translating, so defaults are
// materialised, refines enforced, and an invalid / unknown-action proposal
// is REJECTED fail-closed.
//
// WHY ITS OWN MODULE (INV-1, AGENT-BRIDGE manifest): the capability manifest
// (`buildManifest.ts`) is a PURE DESCRIPTION of the AI surface — it must derive
// from the SAME schema the safe contour enforces, but it must NOT pull in the
// dispatch stack (translators / `applyCommand`). `aiDispatcher.ts` imports every
// domain dispatcher + translator at module scope; importing `aiCommandSchema`
// from THERE would couple `GET /api/manifest` to the whole dispatch graph.
// Defining the union here — from the domain SCHEMA modules only (each imports
// just `zod` + `./blockSchemas`) — keeps the manifest derivation off the
// dispatch path. `aiDispatcher.ts` re-exports these symbols so the existing
// public API is unchanged.
// ═══════════════════════════════════════════════════════════════════════════

import { z } from "zod";

import { blockAiCommandSchema } from "./blockSchemas";
import { entityAiCommandSchema } from "./entitySchemas";
import { drawModifyAiCommandSchema } from "./drawModifySchemas";
import { viewSpaceAiCommandSchema } from "./viewSpaceSchemas";
import { matchpropAiCommandSchema } from "./matchpropSchemas";
import { basePrimDomainAiCommandSchema } from "./basePrimSchemas";
import { layerAiCommandSchema } from "./layerSchemas";

/**
 * The COMBINED discriminated union across all AI domains — the single source of
 * truth for validating AI proposals at the safe-contour boundary AND for
 * deriving the capability manifest. Built by spreading each domain union's
 * `.options` so it stays in lock-step with the domains automatically.
 */
export const aiCommandSchema = z.discriminatedUnion("action", [
  ...blockAiCommandSchema.options,
  ...entityAiCommandSchema.options,
  ...drawModifyAiCommandSchema.options,
  ...viewSpaceAiCommandSchema.options,
  ...matchpropAiCommandSchema.options,
  ...basePrimDomainAiCommandSchema.options,
  ...layerAiCommandSchema.options,
]);

/** A batch of AI proposals — `aiCommandSchema[]`. */
export const aiCommandBatchSchema = z.array(aiCommandSchema);
